import React from "react";
import { motion } from "framer-motion";
import { TrendingDown, Quote } from "lucide-react";

const CASES = [
  {
    industry: "Financial Services",
    pct: 65,
    quote: "Multi-agent trading analysis pipeline — switched from $18K/mo on GPT-4 to $6.3K/mo with mixed routing across 8 providers.",
    saved: "$11,700/mo saved",
    accent: "var(--qn-primary)",
  },
  {
    industry: "Healthcare AI",
    pct: 58,
    quote: "Clinical document processing went from 3 providers with manual switching to 12 providers with automatic routing and failover.",
    saved: "$3,200/mo saved",
    accent: "var(--qn-secondary)",
  },
  {
    industry: "Developer Tools",
    pct: 72,
    quote: "Code generation SaaS moved from single-model Claude to dynamic model selection — costs dropped 72% with no quality loss.",
    saved: "$7,800/mo saved",
    accent: "#A855F7",
  },
  {
    industry: "Education",
    pct: 48,
    quote: "Research paper analysis went from $2.1K/mo with slow turnaround to $1.1K/mo and 2.3x faster processing.",
    saved: "$1,000/mo saved",
    accent: "#F59E0B",
  },
];

export default function SocialProof() {
  return (
    <section className="pb-24 pt-4 relative" data-testid="social-proof-section">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight mb-2">
            Trusted by Forward-Thinking Teams
          </h2>
          <p className="text-sm text-[var(--qn-foreground-muted)] max-w-md mx-auto">
            See how teams across industries are saving with quantum-optimized AI routing.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CASES.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="relative rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 flex flex-col"
              style={{ boxShadow: `0 0 24px -8px ${c.accent}15` }}
              data-testid={`case-study-${i}`}
            >
              {/* Industry tag */}
              <span
                className="self-start text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full mb-4"
                style={{ backgroundColor: `${c.accent}15`, color: c.accent, border: `1px solid ${c.accent}25` }}
              >
                {c.industry}
              </span>

              {/* Savings percentage */}
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 shrink-0" style={{ color: c.accent }} />
                <span className="font-mono font-bold text-2xl" style={{ color: c.accent }}>
                  {c.pct}%
                </span>
                <span className="text-xs text-[var(--qn-foreground-subtle)]">cost reduction</span>
              </div>

              {/* Quote */}
              <div className="flex-1 mb-4">
                <Quote className="w-3.5 h-3.5 text-[var(--qn-foreground-subtle)] mb-1 opacity-40" />
                <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed italic">
                  {c.quote}
                </p>
              </div>

              {/* Saved amount */}
              <div className="text-xs font-mono font-medium pt-3 border-t border-[var(--qn-border)]" style={{ color: c.accent }}>
                {c.saved}
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-[var(--qn-foreground-subtle)] mt-6" data-testid="social-proof-disclaimer">
          * Results based on platform benchmarks and simulated workloads. Individual results may vary.
        </p>
      </div>
    </section>
  );
}
